import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Loader2, Check, BadgeCheck, ShieldCheck, Clock } from "lucide-react";
import useGoerOneAuth from "../../../hooks/useGoerOneAuth";
import {
    fetchInviteStatus,
    verifyPanThunk,
    submitInviteRequest,
    resetInviteForm,
    clearPanError,
} from "../../../store/loyaltySlices/membershipInviteSlice";

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const SOCIALS = [
    { key: "instagram", label: "Instagram", placeholder: "@username" },
    { key: "facebook", label: "Facebook", placeholder: "Profile link or username" },
    { key: "linkedin", label: "LinkedIn", placeholder: "Profile link" },
    { key: "x", label: "X (Twitter)", placeholder: "@username" },
];

const EMPTY_SOCIAL = { instagram: "", facebook: "", linkedin: "", x: "" };

/**
 * "Request consideration" form for invite-only memberships.
 *
 * open:        boolean
 * program:     the invite-only loyalty program being requested
 * onClose:     close handler
 * onSubmitted: optional, called with the program after a request is saved
 */
export default function RequestConsiderationModal({ open, program, onClose, onSubmitted }) {
    const dispatch = useDispatch();
    const { user } = useGoerOneAuth();
    const { statusByProgram, statusLoading, panVerifiedFor, panVerifying, panError, submitting, submitError } = useSelector(
        (state) => state.membershipInvite
    );

    const [pan, setPan] = useState("");
    const [hasCriminalRecord, setHasCriminalRecord] = useState(false); // default "No"
    const [social, setSocial] = useState(EMPTY_SOCIAL);
    const [consent, setConsent] = useState(false);
    const [done, setDone] = useState(false);

    const existing = program ? statusByProgram[program.program_id] : null;

    // Reset and check for an existing request every time the modal opens
    useEffect(() => {
        if (!open || !program) return;
        setPan("");
        setHasCriminalRecord(false);
        setSocial(EMPTY_SOCIAL);
        setConsent(false);
        setDone(false);
        dispatch(resetInviteForm());
        if (user?.id) dispatch(fetchInviteStatus(program.program_id));
    }, [open, program?.program_id, user?.id, dispatch]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open || !program) return null;

    const panValid = PAN_REGEX.test(pan);
    // Verified only for the exact PAN that passed, so editing the number invalidates it automatically
    const panVerified = panValid && panVerifiedFor === pan;
    const canSubmit = panVerified && consent && !submitting && Boolean(user?.id);

    // "checking" | "requested" (pending/approved request already on file) | "done" (just sent) | "form"
    let view = "form";
    if (done) view = "done";
    else if (existing?.requested) view = "requested";
    else if (user?.id && statusLoading && !existing) view = "checking";

    const handlePanChange = (e) => {
        setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10));
        if (panError) dispatch(clearPanError());
    };

    const handleVerify = () => {
        if (!panValid || panVerifying || !user?.id) return;
        dispatch(verifyPanThunk({ pan, customerId: user.id }));
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        const action = await dispatch(
            submitInviteRequest({
                program_id: program.program_id,
                pan_number: pan,
                pan_verified: true,
                consent: true,
                has_criminal_record: hasCriminalRecord,
                social_media: social,
            })
        );
        if (submitInviteRequest.fulfilled.match(action) && !action.payload.alreadyRequested) {
            setDone(true);
            onSubmitted?.(program);
        }
    };

    const verifying = panVerifying;
    const formError = submitError;

    const approved = existing?.invite_status === 1;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Request consideration for ${program.program_name}`}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
            onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
        >
            <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
                <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
                    <div className="min-w-0">
                        <h2 className="text-lg font-extrabold text-gray-900">Request consideration</h2>
                        <p className="truncate text-sm text-gray-500">{program.program_name} is invite only</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="-mr-1 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    >
                        <X size={20} />
                    </button>
                </div>

                {view === "checking" && (
                    <div className="flex items-center justify-center gap-2 px-5 py-14 text-sm text-gray-500">
                        <Loader2 size={18} className="animate-spin" />
                        Checking your requests
                    </div>
                )}

                {view === "requested" && (
                    <div className="px-5 py-10 text-center">
                        <span
                            className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${
                                approved ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"
                            }`}
                        >
                            {approved ? <Check size={24} strokeWidth={3} /> : <Clock size={24} />}
                        </span>
                        <p className="mt-3 text-base font-bold text-gray-900">
                            {approved ? "Your request is approved" : "Already requested"}
                        </p>
                        <p className="mx-auto mt-1 max-w-xs text-sm text-gray-500">
                            {approved
                                ? "You've been cleared for this membership."
                                : "We're reviewing your request for this membership. You'll hear from us once it's decided."}
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-6 rounded-full bg-gray-900 px-6 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
                        >
                            Got it
                        </button>
                    </div>
                )}

                {view === "done" && (
                    <div className="px-5 py-10 text-center">
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                            <Check size={24} strokeWidth={3} />
                        </span>
                        <p className="mt-3 text-base font-bold text-gray-900">Request sent</p>
                        <p className="mx-auto mt-1 max-w-xs text-sm text-gray-500">
                            We'll review your details and let you know about {program.program_name}.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-6 rounded-full bg-gray-900 px-6 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
                        >
                            Done
                        </button>
                    </div>
                )}

                {view === "form" && (
                    <>
                        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
                            {!user?.id && (
                                <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                    Log in to request this membership.
                                </p>
                            )}

                            {/* PAN */}
                            <div>
                                <label htmlFor="rc-pan" className="text-sm font-semibold text-gray-800">
                                    PAN card number
                                </label>
                                <div className="mt-1.5 flex gap-2">
                                    <div className="relative min-w-0 flex-1">
                                        <input
                                            id="rc-pan"
                                            type="text"
                                            value={pan}
                                            onChange={handlePanChange}
                                            maxLength={10}
                                            autoCapitalize="characters"
                                            autoComplete="off"
                                            placeholder="ABCDE1234F"
                                            aria-invalid={Boolean(panError)}
                                            className={`w-full rounded-xl border px-3 py-2.5 pr-9 text-sm font-semibold uppercase tracking-widest outline-none transition-colors focus:ring-2 ${
                                                panVerified
                                                    ? "border-emerald-500 bg-emerald-50 text-emerald-900 focus:ring-emerald-200"
                                                    : panError
                                                      ? "border-red-400 focus:ring-red-100"
                                                      : "border-gray-300 focus:border-[#0B67FC] focus:ring-[#0B67FC]/20"
                                            }`}
                                        />
                                        {panVerified && (
                                            <BadgeCheck size={18} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-600" />
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleVerify}
                                        disabled={!panValid || verifying || panVerified || !user?.id}
                                        className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-all active:scale-[0.98] disabled:cursor-not-allowed ${
                                            panVerified ? "bg-emerald-600" : "bg-[#0B67FC] hover:brightness-95 disabled:opacity-50"
                                        }`}
                                    >
                                        {verifying && <Loader2 size={15} className="animate-spin" />}
                                        {panVerified && <Check size={15} strokeWidth={3} />}
                                        {panVerified ? "Verified" : "Verify PAN"}
                                    </button>
                                </div>
                                {panError ? (
                                    <p className="mt-1.5 text-xs font-medium text-red-600">{panError}</p>
                                ) : panVerified ? (
                                    <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                                        <ShieldCheck size={13} />
                                        PAN verified
                                    </p>
                                ) : pan.length === 10 && !panValid ? (
                                    <p className="mt-1.5 text-xs font-medium text-red-600">Enter a valid PAN, like ABCDE1234F.</p>
                                ) : null}
                            </div>

                            {/* Criminal record */}
                            <fieldset>
                                <legend className="text-sm font-semibold text-gray-800">Do you have a criminal record?</legend>
                                <div className="mt-1.5 grid grid-cols-2 gap-2">
                                    {[
                                        { value: false, label: "No" },
                                        { value: true, label: "Yes" },
                                    ].map((opt) => (
                                        <label
                                            key={opt.label}
                                            className={`flex cursor-pointer items-center justify-center rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                                                hasCriminalRecord === opt.value
                                                    ? "border-[#0B67FC] bg-[#0B67FC]/5 text-[#083F94]"
                                                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="rc-criminal"
                                                className="sr-only"
                                                checked={hasCriminalRecord === opt.value}
                                                onChange={() => setHasCriminalRecord(opt.value)}
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            {/* Social media */}
                            <div>
                                <p className="text-sm font-semibold text-gray-800">
                                    Social media <span className="font-normal text-gray-500">(optional)</span>
                                </p>
                                <div className="mt-1.5 space-y-2">
                                    {SOCIALS.map((s) => (
                                        <input
                                            key={s.key}
                                            type="text"
                                            value={social[s.key]}
                                            onChange={(e) => setSocial((prev) => ({ ...prev, [s.key]: e.target.value }))}
                                            placeholder={`${s.label}: ${s.placeholder}`}
                                            aria-label={s.label}
                                            maxLength={191}
                                            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none transition-colors focus:border-[#0B67FC] focus:ring-2 focus:ring-[#0B67FC]/20"
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Consent: must be ticked (yes) before the request can be sent */}
                            <label
                                className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm transition-colors ${
                                    consent ? "border-emerald-500 bg-emerald-50" : "border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600"
                                />
                                <span className="leading-snug text-gray-700">
                                    <span className="font-semibold text-gray-900">I consent</span> to my PAN and the details above
                                    being checked and reviewed for this membership request.
                                </span>
                            </label>

                            {formError && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{formError}</p>}
                        </div>

                        <div className="border-t border-gray-100 px-5 py-4">
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B67FC] px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:brightness-95 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {submitting && <Loader2 size={16} className="animate-spin" />}
                                Send request
                            </button>
                            {!canSubmit && !submitting && (
                                <p className="mt-2 text-center text-xs text-gray-500">
                                    {!panVerified ? "Verify your PAN to send the request." : "Please give your consent to continue."}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}